import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Button,
  Input,
  Table,
  Badge,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";

/**
 * Lee la orden de compra del cliente (PDF o Excel) y prellena el Nuevo Pedido.
 *
 * Si un modelo de la orden no existe en el catalogo se puede dar de alta desde
 * aqui: el articulo nace con el modelo, la descripcion y los colores y tallas de
 * la orden, y el usuario solo elige los catalogos (departamento, clase, etc.).
 *
 * El back reconoce el formato de cada cliente, y aqui el usuario confirma el
 * cliente y empareja los colores y tallas que no se reconocieron; lo que elige
 * se guarda como equivalencia de ese cliente y la siguiente orden ya llega
 * emparejada. Si un modelo no existe en el catalogo solo se avisa.
 *
 * Una orden con varios modelos se carga completa al mismo pedido, un renglon
 * por articulo, con el boton "Cargar todos al pedido". La lectura se queda en
 * sessionStorage mientras no se cargue, para poder retomarla si se cierra.
 */
const URL_LECTOR = process.env.REACT_APP_URL_LECTOR_PEDIDOS;
const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;
const URL_CATALOGOS = {
  departamentos: process.env.REACT_APP_URL_DEPARTAMENTOS,
  clases: process.env.REACT_APP_URL_CLASES,
  subClases: process.env.REACT_APP_URL_SUBCLASES,
  colecciones: process.env.REACT_APP_URL_COLECCIONES,
  compradores: process.env.REACT_APP_URL_COMPRADORES,
  colaboradores: process.env.REACT_APP_URL_COLABORADORES,
};
// Mismos valores fijos con los que da de alta ArticulosCreateSolo.
const ARTICULO_FIJOS = {
  marcas: "65d8bdeb86de65fdd6715955",
  subDepartamentos: "6578b80b26a9245bd3ddc6de",
  patronista: "641a4dc74f1a3c0014bd77ba",
  muestrista: "641a4dc74f1a3c0014bd77ba",
  ploter: "641a4dc74f1a3c0014bd77ba",
};
const URL_CLIENTES = process.env.REACT_APP_URL_CLIENTES;
const URL_SUCURSALES = process.env.REACT_APP_URL_SUCURSALES;
const URL_GRUPOS = process.env.REACT_APP_URL_GRUPOS;
const URL_LOGS = process.env.REACT_APP_URL_LOGS;

// Alta de cliente: mismos campos y opciones que ClientesCreate.
const CAMPOS_CLIENTE_TEXTO = [
  ["nombre_comercial", "Nombre comercial", 4],
  ["razon_social", "Razón social", 5],
  ["RFC", "RFC", 3],
  ["calle", "Calle", 4],
  ["numero_ext", "No. exterior", 2],
  ["colonia", "Colonia", 3],
  ["delegacion", "Delegación / Municipio", 3],
  ["ciudad", "Ciudad", 3],
  ["estado", "Estado", 3],
  ["cp", "C.P.", 2],
  ["pais", "País", 2],
  ["telefono", "Teléfono", 2],
  ["email", "Email", 4],
  ["codigo", "Código", 2],
  ["condicionesPagoSAT", "Condiciones de pago (SAT)", 4],
];
const OPCIONES_CLIENTE = {
  plazo: ["Plazo (días)", ["0", "30", "60", "90", "120", "150"].map((v) => [v, v])],
  forma_pago: ["Forma de pago", [["Efectivo", "Efectivo"], ["Deposito", "Deposito"], ["Cheque", "Cheque"], ["PorDefinir", "Por Definir"]]],
  regFiscal: ["Régimen fiscal", [
    ["601", "601 General de Ley Personas Morales"],
    ["603", "603 Personas Morales con Fines no Lucrativos"],
    ["605", "605 Sueldos y Salarios"],
    ["608", "608 Demás Ingresos"],
    ["612", "612 Personas Físicas con Actividades Empresariales"],
    ["616", "616 Sin Obligación Fiscal"],
    ["621", "621 Incorporación Fiscal"],
    ["626", "626 Régimen Simplificado de Confianza"],
  ]],
  usoCFDI: ["Uso CFDI", [["G01", "G01 Adquisición de mercancías"], ["G03", "G03 Gastos en general"]]],
  formaPagoSAT: ["Forma de pago (SAT)", [
    ["01", "01 Efectivo"], ["02", "02 Cheque nominativo"], ["03", "03 Transferencia electrónica"],
    ["04", "04 Tarjeta de crédito"], ["28", "28 Tarjeta de débito"], ["99", "99 Por definir"],
  ]],
  metodoPagoSAT: ["Método de pago (SAT)", [["PUE", "PUE (Una sola exhibición)"], ["PPD", "PPD (Parcialidades o diferido)"]]],
};

const CAMPOS_ALTA = [
  ["departamentos", "Departamento"],
  ["clases", "Clase"],
  ["subClases", "Etiqueta"],
  ["colecciones", "Colección"],
  ["colaboradores", "Vendedor"],
  ["compradores", "Comprador"],
];
const CLAVE_SESION = "lectorPedidoCliente";
const CREAR = "__crear__";

const encabezados = () => ({
  headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
});

const leerSesion = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CLAVE_SESION) || "null");
  } catch (e) {
    return null;
  }
};
const guardarSesion = (valor) => {
  try {
    if (valor) sessionStorage.setItem(CLAVE_SESION, JSON.stringify(valor));
    else sessionStorage.removeItem(CLAVE_SESION);
  } catch (e) {
    // Sin sessionStorage solo se pierde el "continuar con el siguiente modelo".
  }
};

const mensajeError = (error) =>
  (error && error.response && error.response.data && error.response.data.message) ||
  "No se pudo leer el pedido";

const formatoNumero = (n) => new Intl.NumberFormat("en-US").format(n || 0);

/** Elecciones del usuario por texto externo: { "Color|STONEALT": "<id>" | CREAR } */
const claveEleccion = (tipo, externo) => `${tipo}|${String(externo || "").trim().toUpperCase()}`;

/** Id del usuario del token, para la bitacora. */
const idUsuario = () => {
  try {
    const parte = String(localStorage.getItem("app_token")).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(decodeURIComponent(escape(atob(parte)))).id;
  } catch (e) {
    return undefined;
  }
};

const idDe = (v) => (Array.isArray(v) ? idDe(v[0]) : v && v._id ? v._id : v || "");

function LectorPedidoCliente({
  clientes,
  colores,
  tallas,
  articulos = [],
  compradores = [],
  colaboradores = [],
  onAplicar,
  onCatalogoCambiado,
}) {
  const archivoRef = useRef(null);
  const [abierto, setAbierto] = useState(false);
  const [leyendo, setLeyendo] = useState(false);
  const [aplicando, setAplicando] = useState(false);
  const [lectura, setLectura] = useState(null);
  const [emparejado, setEmparejado] = useState(null);
  const [cliente, setCliente] = useState("");
  const [elecciones, setElecciones] = useState({});
  const [pendiente, setPendiente] = useState(leerSesion());
  const [alta, setAlta] = useState(null);
  const [guardandoAlta, setGuardandoAlta] = useState(false);
  const [catalogos, setCatalogos] = useState(null);
  const [altaCliente, setAltaCliente] = useState(null);
  const [grupos, setGrupos] = useState([]);
  const [guardandoCliente, setGuardandoCliente] = useState(false);
  // El archivo leido viaja al pedido para guardarse con el (no cabe en sessionStorage).
  const [archivoLeido, setArchivoLeido] = useState(null);

  // Respuesta vigente: al cambiar rapido de cliente solo cuenta la ultima.
  const peticion = useRef(0);

  useEffect(() => {
    setPendiente(leerSesion());
  }, []);

  const aplicarEmparejado = (resultado) => {
    setEmparejado(resultado);
    if (resultado.cliente && resultado.cliente._id) setCliente(resultado.cliente._id);
  };

  const elegirArchivo = () => {
    if (archivoRef.current) {
      archivoRef.current.value = "";
      archivoRef.current.click();
    }
  };

  const leerArchivo = (e) => {
    const archivo = e.target.files && e.target.files[0];
    if (!archivo) return;
    const lector = new FileReader();
    lector.onload = () => {
      setLeyendo(true);
      axios
        .post(`${URL_LECTOR}/leer`, { archivo: lector.result, nombreArchivo: archivo.name }, encabezados())
        .then((res) => {
          setLectura(res.data.lectura);
          setArchivoLeido(archivo);
          setElecciones({});
          setCliente("");
          aplicarEmparejado(res.data.emparejado);
          setAbierto(true);
          guardarSesion({ lectura: res.data.lectura, cliente: res.data.emparejado.cliente ? res.data.emparejado.cliente._id : "" });
          setPendiente(null);
        })
        .catch((error) => {
          Swal.fire("No se pudo leer", mensajeError(error), "error");
        })
        .finally(() => setLeyendo(false));
    };
    lector.onerror = () => Swal.fire("No se pudo leer", "No se pudo abrir el archivo", "error");
    lector.readAsDataURL(archivo);
  };

  const reemparejar = (nuevaLectura, clienteId) => {
    const numero = ++peticion.current;
    return axios
      .post(`${URL_LECTOR}/emparejar`, { lectura: nuevaLectura, clientes: clienteId || undefined }, encabezados())
      .then((res) => {
        if (numero !== peticion.current) return;
        setEmparejado(res.data);
      })
      .catch((error) => {
        if (numero === peticion.current) Swal.fire("Error", mensajeError(error), "error");
      });
  };

  const cambiarCliente = (clienteId) => {
    setCliente(clienteId);
    guardarSesion({ lectura, cliente: clienteId });
    reemparejar(lectura, clienteId);
  };

  const continuarPendiente = () => {
    const sesion = leerSesion();
    if (!sesion || !sesion.lectura) return;
    setLectura(sesion.lectura);
    // Al retomar una lectura guardada ya no se tiene el archivo, salvo que sea la misma de esta pantalla.
    if (!lectura || lectura.pedidoCliente !== sesion.lectura.pedidoCliente) setArchivoLeido(null);
    setElecciones({});
    setCliente(sesion.cliente || "");
    setAbierto(true);
    reemparejar(sesion.lectura, sesion.cliente);
  };

  const descartar = () => {
    guardarSesion(null);
    setPendiente(null);
    setAbierto(false);
    setLectura(null);
    setEmparejado(null);
    setArchivoLeido(null);
  };

  // Color o talla ya resuelto: lo elegido a mano manda sobre lo emparejado.
  const resolver = (tipo, item) => {
    const elegido = elecciones[claveEleccion(tipo, item.externo)];
    if (elegido) return elegido;
    return item.interno || "";
  };

  const elegir = (tipo, externo, valor) => {
    setElecciones({ ...elecciones, [claveEleccion(tipo, externo)]: valor });
  };

  const sinResolver = () => {
    if (!emparejado) return [];
    const faltan = [];
    emparejado.colores.forEach((c) => {
      if (!resolver("Color", c)) faltan.push(`color ${c.externo || "(sin color)"}`);
    });
    emparejado.tallas.forEach((t) => {
      if (!resolver("Talla", t)) faltan.push(`talla ${t.externo}`);
    });
    return faltan;
  };

  const yaCapturado = (modeloEmparejado) => {
    if (!emparejado || !modeloEmparejado || !modeloEmparejado.articulo) return null;
    return (emparejado.duplicados || []).find((p) =>
      (p.articulos || []).includes(modeloEmparejado.articulo._id)
    );
  };

  /**
   * Guarda lo elegido (y crea los colores/tallas nuevos) como equivalencias de
   * este cliente. Devuelve una funcion que da el id final de cada color/talla.
   */
  const guardarElecciones = async () => {
    const equivalencias = [];
    const agregar = (tipo, item) => {
      const valor = resolver(tipo, item);
      if (!item.externo) {
        // "Sin color" en el documento: se usa lo elegido pero no se recuerda.
        return;
      }
      if (valor === CREAR) equivalencias.push({ tipo, externo: item.externo, crear: true });
      else if (item.origen !== "equivalencia" || valor !== item.interno) {
        equivalencias.push({ tipo, externo: item.externo, interno: valor });
      }
    };
    emparejado.colores.forEach((c) => agregar("Color", c));
    emparejado.tallas.forEach((t) => agregar("Talla", t));
    ["Comprador", "Vendedor"].forEach((tipo) => {
      const item = emparejado[tipo.toLowerCase()];
      if (item && item.externo && resolver(tipo, item)) agregar(tipo, item);
    });
    const res = await axios.post(
      `${URL_LECTOR}/equivalencias`,
      { clientes: cliente, claveCliente: lectura.claveCliente, equivalencias },
      encabezados()
    );
    const creados = {};
    (res.data || []).forEach((g) => {
      creados[claveEleccion(g.tipo, g.externo)] = g.interno;
    });
    if (equivalencias.some((e) => e.crear)) {
      // Lo recien creado ya tiene id: se elige ese y se recarga el catalogo.
      const nuevas = { ...elecciones };
      Object.keys(nuevas).forEach((k) => {
        if (nuevas[k] === CREAR && creados[k]) nuevas[k] = creados[k];
      });
      setElecciones(nuevas);
      if (onCatalogoCambiado) await onCatalogoCambiado();
    }
    return (tipo, item) => {
      const valor = resolver(tipo, item);
      if (valor === CREAR) return creados[claveEleccion(tipo, item.externo)];
      return valor;
    };
  };

  const validarParaGuardar = () => {
    if (!cliente) {
      // El selector esta arriba; en ordenes largas queda fuera de la vista.
      const contenedor = document.querySelector(".modal.show");
      if (contenedor) contenedor.scrollTop = 0;
      Swal.fire(
        "Falta el cliente",
        "Elígelo arriba, en «Cliente en el sistema». Si todavía no existe, usa «+ Dar de alta cliente».",
        "warning"
      );
      return false;
    }
    const faltan = sinResolver();
    if (faltan.length) {
      Swal.fire("Falta emparejar", `Elige: ${faltan.join(", ")}`, "warning");
      return false;
    }
    return true;
  };

  const cargarCatalogos = async () => {
    if (catalogos) return catalogos;
    const entradas = await Promise.all(
      Object.entries(URL_CATALOGOS).map(([clave, url]) =>
        axios.get(url, encabezados()).then((r) => [clave, r.data || []])
      )
    );
    const listos = Object.fromEntries(entradas);
    setCatalogos(listos);
    return listos;
  };

  /** Abre el alta del articulo de un modelo que no existe en el catalogo. */
  const abrirAlta = async (indice) => {
    if (!validarParaGuardar()) return;
    const modelo = lectura.modelos[indice];
    setGuardandoAlta(true);
    try {
      const idFinal = await guardarElecciones();
      const listos = await cargarCatalogos();
      // La compradora y el vendedor de la orden mandan sobre los del ultimo articulo.
      // Si la compradora se acaba de dar de alta, se recarga su lista para que aparezca.
      const deLaOrden = {
        compradores: emparejado.comprador ? idFinal("Comprador", emparejado.comprador) || "" : "",
        colaboradores: emparejado.vendedor ? idFinal("Vendedor", emparejado.vendedor) || "" : "",
      };
      if (deLaOrden.compradores && !(listos.compradores || []).some((c) => c._id === deLaOrden.compradores)) {
        const r = await axios.get(URL_CATALOGOS.compradores, encabezados());
        setCatalogos({ ...listos, compradores: r.data || [] });
      }
      const porExterno = (lista, externo) =>
        lista.find((x) => String(x.externo).trim().toUpperCase() === String(externo).trim().toUpperCase());
      const unicos = (arr) => [...new Set(arr.filter(Boolean))];
      const colorIds = unicos(modelo.renglones.map((r) => idFinal("Color", porExterno(emparejado.colores, r.color))));
      const tallaIds = unicos(modelo.renglones.map((r) => idFinal("Talla", porExterno(emparejado.tallas, r.talla))));

      // Departamento, clase, etc. se proponen del ultimo articulo de ese cliente
      // (o del ultimo dado de alta, si el cliente es nuevo); se pueden cambiar.
      const recientes = [...articulos].sort((a, b) =>
        String(b.createdAt || "").localeCompare(String(a.createdAt || ""))
      );
      const plantilla =
        recientes.find((a) => (a.clientes || []).some((c) => idDe(c) === cliente)) || recientes[0] || {};
      const previos = {};
      CAMPOS_ALTA.forEach(([campo]) => {
        previos[campo] = deLaOrden[campo] || idDe(plantilla[campo]);
      });

      setAlta({
        indice,
        codigo: modelo.modelo,
        codigoCliente: modelo.modelo,
        descripcion: modelo.descripcion || modelo.modelo,
        colorIds,
        tallaIds,
        ...previos,
      });
    } catch (error) {
      Swal.fire("Error", mensajeError(error), "error");
    } finally {
      setGuardandoAlta(false);
    }
  };

  const faltanAlta = () => {
    if (!alta) return [];
    const faltan = CAMPOS_ALTA.filter(([campo]) => !alta[campo] || alta[campo] === "0").map(([, etiqueta]) => etiqueta);
    if (!alta.codigo.trim()) faltan.unshift("Modelo provisional");
    if (!alta.descripcion.trim()) faltan.unshift("Descripción");
    if (!alta.colorIds.length) faltan.push("Colores");
    if (!alta.tallaIds.length) faltan.push("Tallas");
    return faltan;
  };

  const guardarAlta = async () => {
    const faltan = faltanAlta();
    if (faltan.length) {
      Swal.fire("Faltan datos", faltan.join(", "), "warning");
      return;
    }
    setGuardandoAlta(true);
    try {
      await axios.post(
        URL_ARTICULOS,
        {
          ...ARTICULO_FIJOS,
          fecha: new Date().toISOString().slice(0, 10),
          descripcion: alta.descripcion.trim(),
          codigo: alta.codigo.trim(),
          codigoCliente: alta.codigoCliente.trim() || alta.codigo.trim(),
          pedido: String(lectura.pedidoCliente || "NA"),
          clientes: cliente,
          colores: alta.colorIds,
          tallas: alta.tallaIds,
          catalogoProcesos: [],
          departamentos: alta.departamentos,
          clases: alta.clases,
          subClases: alta.subClases,
          colecciones: alta.colecciones,
          colaboradores: alta.colaboradores,
          compradores: alta.compradores,
          fotoArticulo: "No",
          costo: 0,
          codigoSAT: "NA",
          codigoUnidad: "NA",
        },
        encabezados()
      );
      // La pantalla necesita el articulo en su lista para poder capturarlo.
      if (onCatalogoCambiado) await onCatalogoCambiado();
      await reemparejar(lectura, cliente);
      setAlta(null);
      Swal.fire("Artículo dado de alta", `Ya puedes capturar el modelo ${alta.codigoCliente || alta.codigo}`, "success");
    } catch (error) {
      Swal.fire("No se pudo dar de alta", mensajeError(error), "error");
    } finally {
      setGuardandoAlta(false);
    }
  };

  /** Alta del cliente de la orden con lo que el documento trae (RFC, direccion...). */
  const abrirAltaCliente = async () => {
    try {
      if (!grupos.length) {
        const res = await axios.get(URL_GRUPOS, encabezados());
        setGrupos(res.data || []);
      }
    } catch (error) {
      Swal.fire("Error", mensajeError(error), "error");
      return;
    }
    const d = (lectura && lectura.datosCliente) || {};
    const nombre = d.nombreComercial || String((lectura && lectura.clienteTexto) || "").split(" (")[0];
    const plazo = d.plazo !== undefined && OPCIONES_CLIENTE.plazo[1].some(([v]) => v === String(d.plazo)) ? String(d.plazo) : "";
    setAltaCliente({
      nombre_comercial: nombre || "",
      razon_social: d.razonSocial || "",
      RFC: d.rfc || "",
      calle: d.calle || "",
      numero_ext: "",
      colonia: "",
      delegacion: "",
      ciudad: d.ciudad || "",
      estado: "",
      cp: "",
      pais: "MEXICO",
      telefono: d.telefono || "",
      email: "",
      codigo: "NA",
      condicionesPagoSAT: plazo ? `Crédito ${plazo} días` : "",
      grupos: "",
      plazo,
      forma_pago: "",
      regFiscal: "",
      usoCFDI: "",
      formaPagoSAT: "",
      metodoPagoSAT: "",
    });
  };

  const faltanCliente = () => {
    if (!altaCliente) return [];
    const faltan = CAMPOS_CLIENTE_TEXTO.filter(([campo]) => !String(altaCliente[campo] || "").trim()).map(
      ([, etiqueta]) => etiqueta
    );
    if (String(altaCliente.cp || "").trim() && !/^\d{5}$/.test(String(altaCliente.cp).trim())) {
      faltan.push("C.P. de 5 dígitos");
    }
    if (!altaCliente.grupos) faltan.push("Zona");
    Object.entries(OPCIONES_CLIENTE).forEach(([campo, [etiqueta]]) => {
      if (!altaCliente[campo]) faltan.push(etiqueta);
    });
    return faltan;
  };

  const guardarAltaCliente = async () => {
    if (faltanCliente().length) return;
    const c = altaCliente;
    const limpio = (campo) => String(c[campo]).trim();
    const yaExiste = clientes.find(
      (x) =>
        String(x.nombre_comercial || "").trim().toUpperCase() === limpio("nombre_comercial").toUpperCase() ||
        (x.RFC && String(x.RFC).trim().toUpperCase() === limpio("RFC").toUpperCase())
    );
    if (yaExiste) {
      Swal.fire("Ya existe", `El cliente ${yaExiste.nombre_comercial} ya está dado de alta; elígelo en la lista.`, "warning");
      return;
    }
    setGuardandoCliente(true);
    try {
      const domicilio = {
        calle: limpio("calle"),
        numero_ext: limpio("numero_ext"),
        numero_int: "-",
        delegacion: limpio("delegacion"),
        estado: limpio("estado"),
        pais: limpio("pais"),
        cp: limpio("cp"),
        colonia: limpio("colonia"),
        ciudad: limpio("ciudad"),
      };
      const res = await axios.post(
        URL_CLIENTES,
        {
          nombre_comercial: limpio("nombre_comercial"),
          razon_social: limpio("razon_social"),
          RFC: limpio("RFC").toUpperCase(),
          ...domicilio,
          telefono: limpio("telefono"),
          email: limpio("email"),
          grupos: c.grupos,
          observaciones: "NA",
          ultimo_pago: "2000-01-01",
          forma_pago: c.forma_pago,
          usoCFDI: c.usoCFDI,
          regFiscal: Number(c.regFiscal),
          formaPagoSAT: c.formaPagoSAT,
          condicionesPagoSAT: limpio("condicionesPagoSAT"),
          metodoPagoSAT: c.metodoPagoSAT,
          codigo: limpio("codigo"),
          plazo: Number(c.plazo),
        },
        encabezados()
      );
      const nuevo = res.data;
      // Igual que ClientesCreate: la sucursal "1" con el domicilio fiscal.
      await axios.post(
        URL_SUCURSALES,
        {
          ...domicilio,
          clientes: nuevo._id,
          alias: "1",
          RFC: limpio("RFC").toUpperCase(),
          razon_social: limpio("razon_social"),
          is_active: "Si",
          regFiscal: Number(c.regFiscal),
        },
        encabezados()
      );
      axios.post(
        URL_LOGS,
        { tipo: "Crear Cliente", detalle: `${limpio("nombre_comercial")} (lector de pedidos)`, user: idUsuario() },
        encabezados()
      );
      // Este formato queda ligado al cliente nuevo.
      if (lectura.claveCliente) {
        await axios.post(
          `${URL_LECTOR}/equivalencias`,
          { clientes: nuevo._id, claveCliente: lectura.claveCliente, equivalencias: [] },
          encabezados()
        );
      }
      if (onCatalogoCambiado) await onCatalogoCambiado();
      setAltaCliente(null);
      cambiarCliente(nuevo._id);
      Swal.fire("Cliente dado de alta", `${limpio("nombre_comercial")} ya quedó seleccionado`, "success");
    } catch (error) {
      Swal.fire("No se pudo dar de alta el cliente", mensajeError(error), "error");
    } finally {
      setGuardandoCliente(false);
    }
  };

  /** Carga al Nuevo Pedido todos los modelos de la orden, un renglon por articulo. */
  const cargarTodos = async () => {
    if (!validarParaGuardar()) return;
    const indices = lectura.modelos.map((m, i) => i);
    const conArticulo = indices.filter((i) => emparejado.modelos[i] && emparejado.modelos[i].articulo);
    const sinArticulo = indices.filter((i) => !conArticulo.includes(i));
    if (!conArticulo.length) {
      Swal.fire("Falta dar de alta", "Ningún modelo de la orden existe en el catálogo; usa «Dar de alta».", "warning");
      return;
    }
    if (sinArticulo.length) {
      const r = await Swal.fire({
        title: "Hay modelos sin dar de alta",
        text: `${sinArticulo.map((i) => lectura.modelos[i].modelo).join(", ")} no existen en el catálogo. ¿Cargar solo los ${conArticulo.length} que sí existen?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, cargar esos",
        cancelButtonText: "No, primero los doy de alta",
      });
      if (!r.value) return;
    }
    const repetidos = conArticulo.map((i) => yaCapturado(emparejado.modelos[i])).filter(Boolean);
    if (repetidos.length) {
      const pedidos = [...new Set(repetidos.map((p) => p.idPedido))].join(", ");
      const r = await Swal.fire({
        title: "Ya está capturado",
        text: `El pedido ${pedidos} ya tiene este pedido del cliente con alguno de estos artículos. ¿Cargarlo otra vez?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, otra vez",
        cancelButtonText: "No",
      });
      if (!r.value) return;
    }

    setAplicando(true);
    try {
      const idFinal = await guardarElecciones();
      const nombreDe = (tipo, id, externo) => {
        const lista = tipo === "Color" ? colores : tallas;
        const x = lista.find((c) => c._id === id);
        return x ? x.name : String(externo || "").toUpperCase();
      };
      const porExterno = (lista, externo) =>
        lista.find((x) => String(x.externo).trim().toUpperCase() === String(externo).trim().toUpperCase());

      const modelos = conArticulo.map((i) => {
        const modelo = lectura.modelos[i];
        return {
          articulo: emparejado.modelos[i].articulo._id,
          codigoCliente: modelo.modelo,
          precio: modelo.costo,
          precioEtiqueta: modelo.precioVenta,
          fechaCancelacion: modelo.fechaCancelacion,
          renglones: modelo.renglones.map((r) => {
            const colorId = idFinal("Color", porExterno(emparejado.colores, r.color));
            const tallaId = idFinal("Talla", porExterno(emparejado.tallas, r.talla));
            return {
              colores: colorId,
              colorNombre: nombreDe("Color", colorId, r.color),
              tallas: tallaId,
              tallaNombre: nombreDe("Talla", tallaId, r.talla),
              cantidad: r.cantidad,
            };
          }),
        };
      });
      // Si cada modelo trae su fecha, el pedido cancela con la mas tardia.
      const fechas = modelos.map((m) => m.fechaCancelacion).filter(Boolean).sort();

      onAplicar({
        cliente,
        pedidoCliente: lectura.pedidoCliente || "",
        fechaPedido: lectura.fechaPedido || "",
        fechaCancelacion: fechas[fechas.length - 1] || lectura.fechaCancelacion || "",
        fechaConfirmacion: lectura.fechaConfirmacion || "",
        // idFinal: si se eligio "Dar de alta", ya trae el id de la compradora nueva.
        comprador: emparejado.comprador ? idFinal("Comprador", emparejado.comprador) || "" : "",
        vendedor: emparejado.vendedor ? idFinal("Vendedor", emparejado.vendedor) || "" : "",
        modelos,
        archivo: archivoLeido,
      });
      guardarSesion(null);
      setPendiente(null);
      setAbierto(false);
    } catch (error) {
      Swal.fire("Error", mensajeError(error), "error");
    } finally {
      setAplicando(false);
    }
  };

  const avisos = [...((lectura && lectura.avisos) || []), ...((emparejado && emparejado.avisos) || [])];

  const selectorCatalogo = (tipo, item) => {
    const lista = tipo === "Color" ? colores : tallas;
    const valor = resolver(tipo, item);
    return (
      <Input
        type="select"
        bsSize="sm"
        value={valor}
        onChange={(e) => elegir(tipo, item.externo, e.target.value)}
      >
        <option value="">Selecciona</option>
        {item.externo ? (
          <option value={CREAR}>+ Crear "{String(item.externo).toUpperCase()}"</option>
        ) : null}
        {[...lista]
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map((c) => (
            <option key={c._id} value={c._id}>
              {c.name}
            </option>
          ))}
      </Input>
    );
  };

  const etiquetaOrigen = (tipo, item) => {
    const elegido = elecciones[claveEleccion(tipo, item.externo)];
    if (elegido === CREAR) return <Badge color="info">Nuevo</Badge>;
    if (elegido) return <Badge color="primary">Elegido</Badge>;
    if (item.origen === "equivalencia") return <Badge color="success">Recordado</Badge>;
    if (item.origen === "nombre") return <Badge color="success">Mismo nombre</Badge>;
    return <Badge color="warning">Elegir</Badge>;
  };

  const tablaEquivalencias = (tipo, lista) => (
    <Table size="sm" bordered className="mb-2">
      <thead>
        <tr>
          <th style={{ width: "35%" }}>{tipo} en el pedido</th>
          <th>{tipo} en el sistema</th>
          <th style={{ width: 110 }}></th>
        </tr>
      </thead>
      <tbody>
        {lista.map((item) => (
          <tr key={`${tipo}-${item.externo}`}>
            <td>{item.externo || <em>(el pedido no dice)</em>}</td>
            <td>{selectorCatalogo(tipo, item)}</td>
            <td className="text-center">{etiquetaOrigen(tipo, item)}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  );

  return (
    <>
      <input
        ref={archivoRef}
        type="file"
        accept=".pdf,.xlsx,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        style={{ display: "none" }}
        onChange={leerArchivo}
      />
      <Button color="primary" onClick={elegirArchivo} disabled={leyendo}>
        {leyendo ? <Spinner size="sm" /> : <i className="fas fa-file-import" />} Leer pedido del cliente
      </Button>
      {pendiente && pendiente.lectura && !abierto ? (
        <Button color="info" className="ml-2" onClick={continuarPendiente}>
          <i className="fas fa-redo" /> Seguir con pedido {pendiente.lectura.pedidoCliente || ""}
        </Button>
      ) : null}

      <Modal size="xl" isOpen={abierto} toggle={() => setAbierto(false)}>
        <ModalHeader toggle={() => setAbierto(false)}>
          Pedido del cliente {lectura ? lectura.pedidoCliente || "" : ""}
        </ModalHeader>
        <ModalBody>
          {lectura && emparejado ? (
            <>
              <Row className="mb-2">
                <Col md={4}>
                  <small className="text-muted">Formato</small>
                  <div>
                    <strong>{lectura.clienteTexto}</strong> ({lectura.formato})
                  </div>
                </Col>
                <Col md={4}>
                  <small className="text-muted">Fechas</small>
                  <div>
                    Pedido {lectura.fechaPedido || "-"} · Cancelación {lectura.fechaCancelacion || "-"}
                    {lectura.fechaConfirmacion ? ` · Confirmación ${lectura.fechaConfirmacion}` : ""}
                  </div>
                </Col>
                <Col md={4}>
                  <small className={cliente ? "text-muted" : "text-danger font-weight-bold"}>
                    Cliente en el sistema{cliente ? "" : " (elige o da de alta)"}
                  </small>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={cliente}
                    invalid={!cliente}
                    onChange={(e) => cambiarCliente(e.target.value)}
                  >
                    <option value="">Selecciona</option>
                    {[...clientes]
                      .sort((a, b) => ((a.nombre_comercial || "") > (b.nombre_comercial || "") ? 1 : -1))
                      .map((c) => (
                        <option key={c._id} value={c._id}>
                          {c.nombre_comercial}
                        </option>
                      ))}
                  </Input>
                  {!cliente ? (
                    <Button size="sm" color="primary" className="mt-1" onClick={abrirAltaCliente}>
                      + Dar de alta cliente
                    </Button>
                  ) : null}
                </Col>
              </Row>

              {emparejado.comprador || emparejado.vendedor ? (
                <Row className="mb-2">
                  {[
                    ["Vendedor", emparejado.vendedor, colaboradores, (x) => [x.nombre, x.apellido].filter(Boolean).join(" ")],
                    ["Comprador", emparejado.comprador, compradores, (x) => x.name || ""],
                  ].map(([tipo, item, lista, nombreDe]) =>
                    item ? (
                      <Col md={6} key={tipo}>
                        <small className="text-muted">
                          {tipo} en la orden: <strong>{item.externo}</strong>
                        </small>
                        <div className="d-flex align-items-center" style={{ gap: 6 }}>
                          <Input
                            type="select"
                            bsSize="sm"
                            value={resolver(tipo, item)}
                            onChange={(e) => elegir(tipo, item.externo, e.target.value)}
                          >
                            <option value="">No existe — lo elijo en el pedido</option>
                            {tipo === "Comprador" && item.externo ? (
                              <option value={CREAR}>+ Dar de alta "{String(item.externo).toUpperCase()}"</option>
                            ) : null}
                            {[...lista]
                              .sort((a, b) => (nombreDe(a) > nombreDe(b) ? 1 : -1))
                              .map((x) => (
                                <option key={x._id} value={x._id}>
                                  {nombreDe(x)}
                                </option>
                              ))}
                          </Input>
                          {etiquetaOrigen(tipo, item)}
                        </div>
                      </Col>
                    ) : null
                  )}
                </Row>
              ) : null}

              {avisos.length ? (
                <div className="alert alert-warning py-2">
                  {avisos.map((a, i) => (
                    <div key={i}>
                      <i className="fas fa-exclamation-triangle" /> {a}
                    </div>
                  ))}
                </div>
              ) : null}

              <Row>
                <Col md={6}>{tablaEquivalencias("Color", emparejado.colores)}</Col>
                <Col md={6}>{tablaEquivalencias("Talla", emparejado.tallas)}</Col>
              </Row>
              <small className="text-muted d-block mb-3">
                Lo que elijas se recuerda para este cliente: la próxima orden ya llega emparejada. ¿Un color o una talla
                no existe? En su lista elige <b>«+ Crear …»</b> y se da de alta al cargar el pedido. La compradora se da de
                alta igual, con <b>«+ Dar de alta …»</b> en su lista.
              </small>

              <Table size="sm" bordered hover>
                <thead>
                  <tr>
                    <th>Modelo</th>
                    <th>Descripción</th>
                    <th className="text-right">Piezas</th>
                    <th className="text-right">Precio</th>
                    <th className="text-right">Etiqueta</th>
                    <th>Cancelación</th>
                    <th>Artículo</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lectura.modelos.map((m, i) => {
                    const emp = emparejado.modelos[i] || {};
                    const duplicado = yaCapturado(emp);
                    const faltantes = [...(emp.coloresFaltantes || []), ...(emp.tallasFaltantes || [])];
                    return (
                      <tr key={`${m.modelo}-${i}`}>
                        <td>
                          <strong>{m.modelo}</strong>
                        </td>
                        <td>
                          {m.descripcion}
                          <div className="text-muted" style={{ fontSize: 12 }}>
                            {m.renglones.map((r) => `${r.color || "-"} ${r.talla}: ${r.cantidad}`).join(" · ")}
                          </div>
                        </td>
                        <td className="text-right">{formatoNumero(m.total)}</td>
                        <td className="text-right">{m.costo !== null ? `$${m.costo}` : "-"}</td>
                        <td className="text-right">{m.precioVenta !== null ? `$${m.precioVenta}` : "-"}</td>
                        <td>{m.fechaCancelacion || "-"}</td>
                        <td>
                          {emp.articulo ? (
                            <>
                              {emp.articulo.codigo}
                              {faltantes.length ? (
                                <div className="text-warning" style={{ fontSize: 12 }}>
                                  No tiene dado de alta: {faltantes.join(", ")} (se agrega solo a este pedido)
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <Badge color="danger">No existe en el catálogo</Badge>
                          )}
                          {duplicado ? (
                            <div>
                              <Badge color="secondary">Capturado en pedido {duplicado.idPedido}</Badge>
                            </div>
                          ) : null}
                        </td>
                        <td>
                          {emp.articulo ? (
                            <Badge color="success">Listo</Badge>
                          ) : (
                            <Button
                              size="sm"
                              color="primary"
                              disabled={guardandoAlta}
                              onClick={() => abrirAlta(i)}
                            >
                              Dar de alta
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
              <div className="text-right">
                <strong>
                  Total {formatoNumero(lectura.total)} pzas.
                  {lectura.totalDeclarado ? ` (el pedido dice ${formatoNumero(lectura.totalDeclarado)})` : ""}
                </strong>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <Spinner />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={async () => {
              const r = await Swal.fire({
                title: "¿Descartar la lectura?",
                text: "Se cierra sin cargar nada al pedido.",
                icon: "question",
                showCancelButton: true,
                confirmButtonText: "Sí, descartar",
                cancelButtonText: "No",
              });
              if (r.value) descartar();
            }}
          >
            Descartar lectura
          </Button>
          <Button color="light" className="ml-auto" onClick={() => setAbierto(false)}>
            Cerrar
          </Button>
          {lectura && emparejado ? (
            <Button color="success" disabled={aplicando} onClick={cargarTodos}>
              {aplicando ? <Spinner size="sm" /> : <i className="fas fa-file-import" />} Cargar todos al pedido
            </Button>
          ) : null}
        </ModalFooter>
      </Modal>

      <Modal size="lg" isOpen={!!alta} toggle={() => !guardandoAlta && setAlta(null)}>
        <ModalHeader toggle={() => !guardandoAlta && setAlta(null)}>Dar de alta el modelo</ModalHeader>
        <ModalBody>
          {alta && catalogos ? (
            <>
              <Row>
                <Col md={4}>
                  <small className="text-muted">Modelo provisional</small>
                  <Input
                    bsSize="sm"
                    value={alta.codigo}
                    onChange={(e) => setAlta({ ...alta, codigo: e.target.value })}
                  />
                </Col>
                <Col md={4}>
                  <small className="text-muted">Modelo oficial (del cliente)</small>
                  <Input
                    bsSize="sm"
                    value={alta.codigoCliente}
                    onChange={(e) => setAlta({ ...alta, codigoCliente: e.target.value })}
                  />
                </Col>
                <Col md={4}>
                  <small className="text-muted">Cliente</small>
                  <Input bsSize="sm" disabled value={(clientes.find((c) => c._id === cliente) || {}).nombre_comercial || ""} />
                </Col>
              </Row>
              <Row className="mt-2">
                <Col md={12}>
                  <small className="text-muted">Descripción</small>
                  <Input
                    bsSize="sm"
                    value={alta.descripcion}
                    onChange={(e) => setAlta({ ...alta, descripcion: e.target.value })}
                  />
                </Col>
              </Row>
              <Row className="mt-2">
                {CAMPOS_ALTA.map(([campo, etiqueta]) => (
                  <Col md={4} key={campo} className="mb-2">
                    <small className="text-muted">{etiqueta}</small>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={alta[campo] || ""}
                      onChange={(e) => setAlta({ ...alta, [campo]: e.target.value })}
                    >
                      <option value="">Selecciona</option>
                      {[...(catalogos[campo] || [])]
                        .map((x) => ({ _id: x._id, nombre: x.name || [x.nombre, x.apellido].filter(Boolean).join(" ") }))
                        .sort((a, b) => (a.nombre > b.nombre ? 1 : -1))
                        .map((x) => (
                          <option key={x._id} value={x._id}>
                            {x.nombre}
                          </option>
                        ))}
                    </Input>
                  </Col>
                ))}
              </Row>
              <div className="mt-2" style={{ fontSize: 13 }}>
                <strong>Colores:</strong>{" "}
                {alta.colorIds.map((id) => (colores.find((c) => c._id === id) || {}).name || "(nuevo)").join(", ")}
                <br />
                <strong>Tallas:</strong>{" "}
                {alta.tallaIds.map((id) => (tallas.find((t) => t._id === id) || {}).name || "(nueva)").join(", ")}
                <div className="text-muted">
                  Salen de la orden del cliente. Foto, procesos y lo demás se completan después en Artículos.
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-4">
              <Spinner />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" disabled={guardandoAlta} onClick={() => setAlta(null)}>
            Cancelar
          </Button>
          {faltanAlta().length ? (
            <span className="text-muted mr-auto" style={{ fontSize: 13 }}>
              Falta: {faltanAlta().join(", ")}
            </span>
          ) : null}
          <Button color="success" disabled={guardandoAlta || !alta || faltanAlta().length > 0} onClick={guardarAlta}>
            {guardandoAlta ? <Spinner size="sm" /> : <i className="fas fa-save" />} Dar de alta
          </Button>
        </ModalFooter>
      </Modal>

      <Modal size="xl" isOpen={!!altaCliente} toggle={() => !guardandoCliente && setAltaCliente(null)}>
        <ModalHeader toggle={() => !guardandoCliente && setAltaCliente(null)}>Dar de alta el cliente</ModalHeader>
        <ModalBody>
          {altaCliente ? (
            <>
              <Row>
                {CAMPOS_CLIENTE_TEXTO.map(([campo, etiqueta, ancho]) => (
                  <Col md={ancho} key={campo} className="mb-2">
                    <small className="text-muted">{etiqueta}</small>
                    <Input
                      bsSize="sm"
                      value={altaCliente[campo]}
                      invalid={!String(altaCliente[campo] || "").trim()}
                      onChange={(e) => setAltaCliente({ ...altaCliente, [campo]: e.target.value })}
                    />
                  </Col>
                ))}
                <Col md={3} className="mb-2">
                  <small className="text-muted">Zona</small>
                  <Input
                    type="select"
                    bsSize="sm"
                    value={altaCliente.grupos}
                    invalid={!altaCliente.grupos}
                    onChange={(e) => setAltaCliente({ ...altaCliente, grupos: e.target.value })}
                  >
                    <option value="">Selecciona</option>
                    {[...grupos]
                      .sort((a, b) => (a.name > b.name ? 1 : -1))
                      .map((g) => (
                        <option key={g._id} value={g._id}>
                          {g.name}
                        </option>
                      ))}
                  </Input>
                </Col>
                {Object.entries(OPCIONES_CLIENTE).map(([campo, [etiqueta, opciones]]) => (
                  <Col md={3} key={campo} className="mb-2">
                    <small className="text-muted">{etiqueta}</small>
                    <Input
                      type="select"
                      bsSize="sm"
                      value={altaCliente[campo]}
                      invalid={!altaCliente[campo]}
                      onChange={(e) => setAltaCliente({ ...altaCliente, [campo]: e.target.value })}
                    >
                      <option value="">Selecciona</option>
                      {opciones.map(([v, t]) => (
                        <option key={v} value={v}>
                          {t}
                        </option>
                      ))}
                    </Input>
                  </Col>
                ))}
              </Row>
              <small className="text-muted">
                Lo que venía en la orden ya está lleno; revisa los datos fiscales antes de facturar.
              </small>
            </>
          ) : null}
        </ModalBody>
        <ModalFooter>
          {faltanCliente().length ? (
            <span className="text-muted mr-auto" style={{ fontSize: 13 }}>
              Falta: {faltanCliente().join(", ")}
            </span>
          ) : null}
          <Button color="light" disabled={guardandoCliente} onClick={() => setAltaCliente(null)}>
            Cancelar
          </Button>
          <Button
            color="success"
            disabled={guardandoCliente || faltanCliente().length > 0}
            onClick={guardarAltaCliente}
          >
            {guardandoCliente ? <Spinner size="sm" /> : <i className="fas fa-save" />} Dar de alta
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}

export default LectorPedidoCliente;
